import {
  Table as MuiTable,
  TableProps as MuiTableProps,
  makeStyles,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Theme,
  Box,
  createStyles,
} from "@material-ui/core";

import { BaseRow } from "~components/TableRows/Base";

import { TableLoadingState } from "./tableLoadingState";
import TableEmptyState from "./tableEmptyState";
import { Order } from "./helpers";

export type TableItem = {
  id: string | number;
};

export type TableRowConfig<T> = {
  activeRow: T | undefined;
  keys: {
    key: string;
    label: string;
    isSortable: boolean;
    visibleWhenCondensed: boolean;
    isNumeric?: boolean | undefined;
  }[];
  onRowClick: (data: T) => void;
  onActionClick?: (data: T) => void;
  isCondensed: string | number | undefined;
};

export type TableProps<T extends TableItem> = {
  /** [ui] columns to render */
  columns: {
    key: string;
    label: string;
    /** [state] sortable indicator */
    isSortable: boolean;
    /** [state] visible when detail view is visible */
    visibleWhenCondensed: boolean;
    /** [ui] numeric indicator (used to rightAlign the cell/header) */
    isNumeric?: boolean;
  }[];

  /** [state] info required for a table with pagination */
  items: {
    data: T[];
    limit: number;
    page: number;
    totalCount: number;
  };
  /** [state] current sort key and sort order ('asc'|'desc') */
  sort:
    | {
        key: string;
        order: Order;
      }
    | undefined;
  /** [state] loading state indicator */
  isLoading: boolean;
  /** [state] active table row */
  activeRow: T | undefined;

  /** [handlers] sort */
  onSortChange: (key: string, order: "desc" | "asc") => void;
  /** [handlers] pageIndex (offset) */
  onChangePage: (newIndex: number) => void;
  /** [handlers] rowsPerPage (limit) */
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  /** [handlers] row click */
  onRowClick: (data: T) => void;

  /** [handlers] optional action handler (control for the hover icon onClick handler) */
  onActionClick?: (data: T) => void;

  /** if not provided a base row is rendered */
  renderRow?: (data: T, config: TableRowConfig<T>) => JSX.Element;

  classes?: Partial<ReturnType<typeof useStyles>>;
} & MuiTableProps;

export const Table = <T extends TableItem>({
  items,
  onRowClick,
  onActionClick,
  activeRow,
  renderRow,
  ...props
}: TableProps<T>) => {
  const classes = useStyles(props);
  const { onSortChange, sort, onChangeRowsPerPage, onChangePage, columns, isLoading } = props;

  const handleChangePage = (_: unknown, newPage: number) => onChangePage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeRowsPerPage(+event.target.value);
    onChangePage(0);
  };

  const handleRequestSort = (property: string) => {
    const orderKey = property;
    const isAsc = sort?.key === orderKey && sort?.order === "asc";

    onSortChange(orderKey, isAsc ? "desc" : "asc");
  };

  if (isLoading) {
    return (
      <TableContainer component={Paper} className={classes.container}>
        <TableLoadingState limit={items.limit} />
      </TableContainer>
    );
  }

  const isCondensed = activeRow && activeRow.id;
  const keys = isCondensed ? columns.filter((col) => !!col.visibleWhenCondensed) : columns;

  return (
    <TableContainer component={Paper} className={classes.container}>
      {items.data.length <= 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          minHeight={328}
          style={{
            paddingTop: 88,
            boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.2)",
          }}
        >
          <TableEmptyState />
          <Typography className={classes.noResultsText}>No results to show.</Typography>
        </Box>
      ) : (
        <MuiTable aria-label="transactions table" stickyHeader padding="none">
          <TableHead className={classes.tableHead}>
            <TableRow>
              {keys.map((column) => (
                <TableCell
                  className={classes.h7}
                  key={column.key}
                  sortDirection={column.key === sort?.key ? sort?.order : false}
                  align={column.isNumeric ? "right" : undefined}
                >
                  {column.isSortable ? (
                    <TableSortLabel
                      active={column.key === sort?.key}
                      direction={column.key === sort?.key ? sort?.order : "desc"}
                      onClick={() => handleRequestSort(column.key)}
                      className={classes.tableSortLabel}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <span className={classes.tableSortLabel}>{column.label}</span>
                  )}
                </TableCell>
              ))}
              {!isCondensed && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.data.map((row) =>
              typeof renderRow === "function" ? (
                renderRow(row, {
                  activeRow,
                  keys,
                  isCondensed,
                  onRowClick,
                  onActionClick,
                })
              ) : (
                <BaseRow
                  row={row}
                  activeRow={activeRow}
                  keys={keys}
                  isCondensed={isCondensed}
                  onRowClick={onRowClick}
                  onActionClick={onActionClick}
                />
              ),
            )}
          </TableBody>
        </MuiTable>
      )}
      {items.data.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={items.totalCount}
          rowsPerPage={items.limit}
          page={items.page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          className={classes.pagination}
          nextIconButtonProps={{
            color: "primary",
            className: classes.paginationButton,
          }}
          backIconButtonProps={{
            color: "primary",
            className: classes.paginationButton,
          }}
          classes={{
            select: classes.select,
            selectIcon: classes.selectIcon,
          }}
          labelRowsPerPage={
            <Typography component="span" className={classes.paginationText}>
              Rows per page
            </Typography>
          }
          labelDisplayedRows={() => (
            <Typography component="span" className={classes.paginationText}>
              Page <b>{`${items.page + 1}`}</b> of{" "}
              <b>{`${Math.ceil(items.totalCount / items.limit)}`}</b>
            </Typography>
          )}
        />
      )}
    </TableContainer>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      overflowX: "unset",

      boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.2)",

      maxHeight: "100%",
      overflowY: "auto",
    },
    noResultsText: {
      marginTop: theme.spacing(3),
      fontSize: 18,
      lineHeight: 1.33,
      color: theme.palette.dark.main,
    },
    tableSortLabel: {
      "& svg": {
        fontSize: 16,
      },

      "&:hover": {
        transition: "color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      },
    },
    h7: {
      textTransform: "uppercase",
      fontSize: "12px",
      fontWeight: "bold",
      lineHeight: "16px",
      color: theme.palette.battleshipGrey.main,
    },
    pagination: {
      fontSize: "14px",
      lineHeight: 1.36,
      color: theme.palette.dark.main,
      boxShadow: "0 -1px 0 0 #f7f7f7",

      "&> div": {
        minHeight: "auto",

        padding: theme.spacing(1),
        paddingRight: 0,
      },
    },
    paginationText: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1.36,
    },
    paginationButton: {
      marginRight: "16px",
      padding: "8px",
      borderRadius: "2px",
      boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",
      width: 32,
      height: 32,
    },
    select: {
      backgroundColor: "white",
      marginLeft: 16,
      padding: theme.spacing(1),
      borderRadius: 2,
      boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",
      textAlignLast: "center",
    },
    selectIcon: {
      color: theme.palette.primary.main,
      fontSize: 24,
    },
    tableHead: {
      borderRadius: 4,

      "& th": {
        "&:first-child": {
          borderTopLeftRadius: 4,
        },
        "&:last-child": {
          borderTopRightRadius: 4,
        },

        position: "sticky",
        backgroundColor: "white",
        top: 0,

        padding: 16,

        border: 0,
      },
    },
  }),
);
